import React, { ButtonHTMLAttributes } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'brandTint'
  | 'warning'
  | 'danger'
  | 'ghost'
  /** @deprecated Alias of `secondary` — the guide has one neutral button, not two. */
  | 'outline';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'iconTight';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  loadingText?: string;
}

/**
 * Buttons implement `docs/design-system/elevated-polish-guide.md`, with one
 * deliberate departure recorded below.
 *
 * The guide specifies `ring-1` rather than `border` throughout — a ring paints
 * outside the box, so edges stay crisp and swapping variants never shifts
 * layout by a pixel. It also specifies `font-bold`; `font-semibold` is called
 * out in the guide as reading "almost-bold and weak".
 */
const BUTTON_BASE_CLASSES = [
  'inline-flex items-center justify-center gap-1.5',
  'font-bold rounded-xl whitespace-nowrap',
  'transition-colors duration-150',
  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3FA5FF]',
  'disabled:opacity-50 disabled:cursor-not-allowed',
].join(' ');

/** The guide's neutral button. `secondary` and `outline` are the same thing. */
const NEUTRAL =
  'bg-white ring-1 ring-slate-200 text-slate-700 shadow-sm hover:bg-slate-50 hover:ring-slate-300 active:bg-slate-100';

const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  /**
   * DEPARTURE FROM THE GUIDE, DELIBERATE (Dillan, 2026-09-07).
   *
   * The guide specifies a gradient primary — `bg-gradient-to-br from-[#3FA5FF]
   * to-[#007ACC]`, uppercase, font-black, lift-on-hover. We keep the flat fill:
   * the gradient reads as decoration rather than as hierarchy, and every other
   * variant below IS taken from the guide, so the ranking still works without
   * it. Revisit only as an explicit visual decision, not as a "correction".
   */
  primary: 'bg-[#007ACC] text-white hover:bg-[#005fa3] active:bg-[#004a80]',
  // Guide: "Navigate, View, Cancel". Neutral, not brand-tinted.
  secondary: NEUTRAL,
  /** @deprecated Use `secondary`. Kept so existing call sites keep working. */
  outline: NEUTRAL,
  // Guide: "In-brand action, not primary" — reads as brand without competing.
  brandTint: 'bg-[#3FA5FF]/10 ring-1 ring-[#3FA5FF]/30 text-[#007ACC] hover:bg-[#3FA5FF]/20',
  // Guide: "Warning / briefing / draft" — resolve a gap, review a mismatch.
  warning: 'bg-amber-50 ring-1 ring-amber-200 text-amber-800 hover:bg-amber-100',
  // Guide: "Destructive / error". Softer than a solid red on purpose — the
  // guide reserves loud for the primary CTA, so red carries meaning, not volume.
  danger: 'bg-red-50 ring-1 ring-red-200 text-red-800 hover:bg-red-100',
  // No guide equivalent. Cancel links and low-priority text actions still need
  // a button with no surface of its own.
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200',
};

const BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  /**
   * INLINE size — a button that sits inside a line of text rather than in an
   * action row, such as the "+ Add" affordance beside a kanban card's notes
   * count. Nothing smaller than `sm` existed, so call sites were reaching for
   * `!px-1.5 !py-0 !min-h-0` important-overrides to force one, which defeats
   * the point of the component owning its styling and drifts per call site.
   *
   * It is DELIBERATELY SMALLER THAN EVERY OTHER SIZE HERE, and that is the
   * whole point: a dense in-card secondary control, sitting beside a much
   * larger primary target (the card itself), where an `sm` control would
   * crowd the row it labels. It is NOT a standalone touch target — do not
   * reach for it for an action that has to be hit on its own. Use `sm`.
   * (An earlier version of this comment cited a "32px touch target" from
   * `elevated-polish-guide.md`; the guide states no such figure.)
   */
  xs: 'px-1.5 py-0.5 text-xs min-h-[20px]',
  sm: 'px-3 py-1.5 text-sm min-h-[32px]',
  md: 'px-4 py-2 text-sm min-h-[36px]',
  lg: 'px-5 py-2.5 text-sm min-h-[44px]',
  icon: 'p-2 min-h-[36px] min-w-[36px]',
  /**
   * An icon with a 2px margin and no minimum box (ISS-588). DELIBERATELY BELOW the usual mobile
   * tap-target size, by Nick's direction for the lead card pin: the 36px box pushed every card's name
   * aside. Do not "fix" it back to 36/44px. Use it only for a dense in-card control whose host (the
   * card) is itself the large target; anything that must be hit on its own uses `icon`.
   */
  iconTight: 'p-0.5 min-h-0 min-w-0',
};

/**
 * The exact class list <Button> renders for a given variant/size.
 *
 * Exported for the rare element that must look like a Button but cannot be
 * one — an anchor, for instance. Use this instead of copying the class string,
 * so link-shaped buttons follow Button when its styling changes.
 */
export function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extraClasses = ''
): string {
  return [
    BUTTON_BASE_CLASSES,
    BUTTON_VARIANT_CLASSES[variant],
    BUTTON_SIZE_CLASSES[size],
    extraClasses,
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * FORWARDS ITS REF, and that is load-bearing rather than tidiness.
 *
 * A popover trigger needs the DOM node to position a menu against and to answer "was the
 * click outside?". Without a forwarded ref, every such trigger in this codebase had two
 * options: wrap the button in an extra positioning `<div>`, or hand-roll the button's
 * classes so it could own the ref itself. `StageMenu.tsx` took the second route and grew a
 * copy of this file's NEUTRAL string, which is exactly the hand-rolled-button drift
 * CLAUDE.md forbids — and a copy that cannot be kept in sync by the type system.
 *
 * One `forwardRef` removes the reason to ever do that again. Purely additive: every existing
 * call site is unaffected.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    loadingText,
    children,
    className = '',
    disabled,
    ...props
  },
  ref
) {
  const classes = buttonClasses(
    variant,
    size,
    [fullWidth ? 'w-full' : '', className].join(' ')
  );

  return (
    <button ref={ref} className={classes} disabled={disabled || loading} {...props}>
      {loading && (
        <svg
          className="animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {loading && loadingText ? loadingText : children}
    </button>
  );
});
